import type { MatchupsResponse, MatchupSide } from '../../shared/types';
import type { WeekAnalysis } from './projections';

/**
 * One week reduced to the handful of facts worth repeating.
 *
 * Exists as a library rather than inside the view for the usual reason here:
 * nothing in this file imports React, so the render harness and the esbuild
 * fixtures can both reach it.
 *
 * Everything is derived from a single `/api/matchups` response, which already
 * carries per-player scores. The lineup ledger is an OPTIONAL second input --
 * it adds the decision-quality lines, and it is genuinely absent for the week
 * in progress, because it is built nightly.
 */

export interface RecapSide {
  rosterId: number;
  points: number;
  benchPoints: number;
}

export interface RecapGame {
  matchupId: number;
  sides: RecapSide[];
  /** Null when the two sides tied, or when a roster had no opponent. */
  winner: number | null;
  loser: number | null;
  margin: number | null;
  combined: number;
}

export interface RecapPlayer {
  playerId: string;
  rosterId: number;
  points: number;
}

/** A superlative: the extreme, plus how far clear of the field it was. */
export interface Extreme<T> {
  value: T;
  /** Distance to the runner-up, for the rare case a page wants to say so. */
  clearBy: number | null;
}

export interface Recap {
  season: string;
  week: number;
  played: boolean;
  isPlayoff: boolean;
  games: RecapGame[];
  /** Every scoring roster, highest first. */
  standings: RecapSide[];
  highest: Extreme<RecapSide> | null;
  lowest: Extreme<RecapSide> | null;
  blowout: RecapGame | null;
  closest: RecapGame | null;
  highestCombined: RecapGame | null;
  /** Best individual performances, highest first. */
  topPlayers: RecapPlayer[];
  /** Most points left on the bench. */
  benchLeader: RecapSide | null;
  leagueMean: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Turns one week's matchups into its story.
 *
 * A week with no scores yet returns `played: false` and empty superlatives
 * rather than a set of 0.00 ties. Sleeper answers a future week with ten fully
 * formed rows scoring nothing, so "are there rows" is never the test.
 */
export function buildRecap(matchups: MatchupsResponse | null): Recap | null {
  if (!matchups) return null;

  const games: RecapGame[] = [];
  const sides: RecapSide[] = [];

  for (const pair of matchups.matchups) {
    const theseSides: RecapSide[] = pair.teams.map((t: MatchupSide) => ({
      rosterId: t.rosterId,
      points: t.points,
      benchPoints: t.benchPoints,
    }));
    sides.push(...theseSides);

    if (theseSides.length !== 2) {
      games.push({
        matchupId: pair.matchupId,
        sides: theseSides,
        winner: null,
        loser: null,
        margin: null,
        combined: round2(theseSides.reduce((t, s) => t + s.points, 0)),
      });
      continue;
    }

    const [a, b] = [...theseSides].sort((x, y) => y.points - x.points);
    const tied = a.points === b.points;
    games.push({
      matchupId: pair.matchupId,
      sides: theseSides,
      winner: tied ? null : a.rosterId,
      loser: tied ? null : b.rosterId,
      margin: round2(a.points - b.points),
      combined: round2(a.points + b.points),
    });
  }

  const played = matchups.played;
  const ranked = [...sides].sort((a, b) => b.points - a.points);
  const decided = games.filter((g) => g.margin !== null && g.sides.length === 2);

  const extreme = <T,>(list: T[], value: (t: T) => number, best: 'max' | 'min'): Extreme<T> | null => {
    if (list.length === 0) return null;
    const sorted = [...list].sort((x, y) =>
      best === 'max' ? value(y) - value(x) : value(x) - value(y),
    );
    return {
      value: sorted[0],
      clearBy: sorted.length > 1 ? round2(Math.abs(value(sorted[0]) - value(sorted[1]))) : null,
    };
  };

  const players: RecapPlayer[] = [];
  for (const pair of matchups.matchups) {
    for (const side of pair.teams) {
      for (const s of side.starters) {
        // "0" is Sleeper's empty-slot marker, not a player id.
        if (!s.playerId || s.playerId === '0') continue;
        players.push({ playerId: s.playerId, rosterId: side.rosterId, points: s.points });
      }
    }
  }
  players.sort((a, b) => b.points - a.points);

  const mean =
    sides.length === 0 ? 0 : round2(sides.reduce((t, s) => t + s.points, 0) / sides.length);

  return {
    season: matchups.season,
    week: matchups.week,
    played,
    isPlayoff: matchups.isPlayoff,
    games,
    standings: ranked,
    highest: played ? extreme(sides, (s) => s.points, 'max') : null,
    lowest: played ? extreme(sides, (s) => s.points, 'min') : null,
    blowout: played ? (extreme(decided, (g) => g.margin ?? 0, 'max')?.value ?? null) : null,
    closest: played ? (extreme(decided, (g) => g.margin ?? 0, 'min')?.value ?? null) : null,
    highestCombined: played ? (extreme(decided, (g) => g.combined, 'max')?.value ?? null) : null,
    topPlayers: played ? players.slice(0, 5) : [],
    benchLeader: played ? (extreme(sides, (s) => s.benchPoints, 'max')?.value ?? null) : null,
    leagueMean: mean,
  };
}

// --- Decision quality, when the ledger covers the week ----------------------

export interface RecapCoaching {
  /** Beat the projections by the most -- the week's best call. */
  bestCall: WeekAnalysis | null;
  /** Left the most foreseeable points on the bench. */
  worstCall: WeekAnalysis | null;
  /** Most points nothing could have predicted. */
  unluckiest: WeekAnalysis | null;
  /** Closest to the perfect lineup, as a share of it. */
  mostEfficient: WeekAnalysis | null;
  weeks: WeekAnalysis[];
}

/**
 * The lineup-decision lines for one week.
 *
 * `weeks` should already be filtered to the season and week in question. Returns
 * nulls rather than an empty object so a caller can render each line
 * independently -- the ledger is built nightly, so the week in progress has
 * none of this while every finished week has all of it.
 */
export function recapCoaching(weeks: WeekAnalysis[]): RecapCoaching {
  if (weeks.length === 0) {
    return { bestCall: null, worstCall: null, unluckiest: null, mostEfficient: null, weeks };
  }

  const byCost = [...weeks].sort((a, b) => a.coachingCost - b.coachingCost);
  const byUnforeseeable = [...weeks].sort((a, b) => b.unforeseeable - a.unforeseeable);
  // Share of the perfect lineup actually captured. A roster whose perfect
  // lineup scored nothing is excluded rather than counted as 100%.
  const efficient = weeks
    .filter((w) => w.perfect > 0)
    .sort((a, b) => b.actual / b.perfect - a.actual / a.perfect);

  return {
    // Only count it as a "best call" if they actually beat the chalk; on a week
    // where every manager underperformed, the least-bad is not a good decision.
    bestCall: byCost[0] && byCost[0].coachingCost < 0 ? byCost[0] : null,
    worstCall:
      byCost[byCost.length - 1] && byCost[byCost.length - 1].coachingCost > 0
        ? byCost[byCost.length - 1]
        : null,
    unluckiest: byUnforeseeable[0] ?? null,
    mostEfficient: efficient[0] ?? null,
    weeks,
  };
}
